import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SignDetailsRequest } from './requests/sign-details.request';
import { UserResponse } from './responses/user.response';
import { map, tap } from 'rxjs/operators';
import { StorageMap } from '@ngx-pwa/local-storage';
import { AuthResponse } from './responses/auth.response';
import { IEnvironment } from '../environment/environment.interface';
import { RegDetailsRequest } from './requests/reg-detail.request';
import { AuthServiceInterface } from './auth-service.interface';

@Injectable()
export class AuthService implements AuthServiceInterface {
  private endpointApi: string;
  private user: UserResponse;
  public otp$ = new BehaviorSubject(false);
  private action = 'AffiliateCrm\\Affiliate\\ContactAdministrator';
  private contactManagerAction = 'AffiliateCrm\\Affiliate\\ActivateAffiliate';
  private repository = 'Eloquent\\AffiliateRepository';

  constructor(
    private http: HttpClient,
    protected localStorage: StorageMap,
    @Inject('IEnvironmentService') private envService: IEnvironment
  ) {
    this.endpointApi = this.envService.getVar('services.api') as string;
  }

  public isAuthorized(): boolean {
    return !!localStorage.getItem('affiliate-token');
  }

  public isActiveUser(): boolean {
    return localStorage.getItem('affiliate-active-user') === '1';
  }

  public userData(): UserResponse {
    return this.user;
  }

  public getUserAffiliateId(): number {
    return +Object.keys(this.user.affiliates)[0];
  }

  public setUser(user: UserResponse): void {
    this.user = user;
  }

  public auth(signDetailsRequest: SignDetailsRequest, succesCb: (user: UserResponse) => void, errorCb: (error: any) => void): void {
    const form: SignDetailsRequest = {
      email: signDetailsRequest.email,
      password: signDetailsRequest.password,
      agreementIds: signDetailsRequest.agreementIds,
    };

    if (signDetailsRequest?.token_2fa) {
      form.token_2fa = signDetailsRequest.token_2fa;
    }

    this.http
      .post<AuthResponse>(`${this.endpointApi}/sign/in`, form)
      .pipe(map((data) => AuthResponse.fromJson(data)))
      .subscribe(
        (response) => {
          localStorage.setItem('affiliate-token', response.token.token);
          localStorage.setItem('affiliate-active-user', response.user.status);
          this.setUser(response.user);
          succesCb(this.user);
        },
        (error) => {
          errorCb(error);
        }
      );
  }

  public reg(regDetailsRequest: RegDetailsRequest, succesCb: (user: UserResponse) => void, errorCb: (error: any) => void): void {
    const form = {
      affiliate_name: regDetailsRequest.affiliate_name,
      email: regDetailsRequest.email,
      password: regDetailsRequest.password,
      password_confirmation: regDetailsRequest.password_confirmation,
      crm_terms: regDetailsRequest.crm_terms,
      aff_manager_id: regDetailsRequest.aff_manager_id,
      answers: regDetailsRequest.answers,
      aff_group_id: regDetailsRequest.aff_group_id,
      hash: regDetailsRequest.hash,
    };

    this.http
      .post<AuthResponse>(`${this.endpointApi}/sign/up`, form)
      .pipe(map((data) => AuthResponse.fromJson(data)))
      .subscribe(
        (response) => {
          localStorage.setItem('affiliate-token', response.token.token);
          localStorage.setItem('affiliate-active-user', response.user.status);
          this.setUser(response.user);
          succesCb(this.user);
        },
        (error) => {
          errorCb(error);
        }
      );
  }

  public getUser(): Observable<UserResponse> {
    return this.http.get<UserResponse>(`${this.endpointApi}/sign/user`).pipe(
      map((data) => UserResponse.fromJson(data)),
      tap((data) => {
        this.otp$.next(data?.otp);
        localStorage.setItem('affiliate-active-user', data?.status);
      })
    );
  }

  public logout(): void {
    localStorage.removeItem('affiliate-token');
  }

  public contactToAdmin(): Observable<any> {
    const requestParams: any = {
      action: this.action,
      repository: this.repository,
    };
    return this.http.post(`${this.endpointApi}/action/process`, requestParams);
  }

  public contactToManager(): Observable<any> {
    const requestParams: any = {
      action: this.contactManagerAction,
      repository: this.repository,
    };
    return this.http.post(`${this.endpointApi}/action/process`, requestParams);
  }
}
